import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface TenantBrand {
  id: string;
  subdomain: string;
  name: string;
  contactEmail: string;
  contactPhone?: string;
  streetAddress?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  languageDefault: string;
  languagesEnabled: { [key: string]: boolean };
  rtl: boolean;
  logoUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  backgroundColor: string;
  instagramUrl?: string;
  whatsappUrl?: string;
  googleReviewsUrl?: string;
  websiteUrl?: string;
  subscriptionPlan: 'starter' | 'pro' | 'enterprise';
  billingStatus: string;
  monthlyQuota: number;
  quotaUsed: number;
  frontdeskEmail?: string;
  skinFlowEnabled: boolean;
  hairFlowEnabled: boolean;
  trustSectionEnabled: boolean;
}

interface UserRole {
  tenantId: string;
  tenantName: string;
  role: 'owner' | 'admin' | 'analyst' | 'viewer';
}

interface TenantContextType {
  currentTenant: TenantBrand | null;
  userTenants: UserRole[];
  userRole: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  refreshTenant: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

const TENANT_STORAGE_KEY = 'savoy.current_tenant_id';
const TENANT_CACHE_KEY = 'savoy.tenant_cache';
const USER_TENANTS_CACHE_KEY = 'savoy.user_tenants_cache';
const USER_ROLE_CACHE_KEY = 'savoy.user_role_cache';

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<TenantBrand | null>(null);
  const [userTenants, setUserTenants] = useState<UserRole[]>([]);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const loadingRef = useRef(false); // Prevent concurrent loads
  const hasLoadedOnceRef = useRef(false); // Prevent any reloads after first successful load
  const { toast } = useToast();

  const loadTenantData = async (userId: string, tenantId?: string, retryCount = 0) => {
    // Prevent concurrent loads
    if (loadingRef.current) {
      return;
    }
    loadingRef.current = true;
    setIsLoading(true);
    
    const safetyTimeout = setTimeout(() => {
      console.error('⏱️ Safety timeout reached, forcing initialization');
      loadingRef.current = false;
      setIsLoading(false);
      setIsInitialized(true);
    }, 60000); // 60 seconds safety timeout
    
    try {
      // Check for impersonation mode (sessionStorage only - tab-isolated)
      const impersonationMode = sessionStorage.getItem('impersonation_mode');
      const impersonationTenant = sessionStorage.getItem('impersonation_tenant');
      const impersonationToken = sessionStorage.getItem('impersonation_token');
      
      const isImpersonating = !!(impersonationMode && impersonationTenant);
      
      if (isImpersonating) {
        console.log('🎭 Impersonation mode detected, loading tenant:', impersonationTenant);
        
        // Load the impersonated tenant directly without checking user roles
        const { data: tenantData, error: tenantError } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', impersonationTenant)
          .single();
        
        if (tenantError || !tenantData) {
          console.error('❌ Failed to load impersonated tenant:', tenantError);
          throw new Error('Failed to load impersonated tenant');
        }
        
        console.log('📋 Loaded impersonated tenant data:', {
          name: tenantData.name,
          logo: tenantData.logo_url,
          colors: {
            primary: tenantData.primary_color,
            secondary: tenantData.secondary_color,
            background: tenantData.background_color
          },
          contact: {
            email: tenantData.contact_email,
            phone: tenantData.contact_phone,
            address: tenantData.street_address,
            city: tenantData.city
          }
        });
        
        const brand: TenantBrand = {
          id: tenantData.id,
          subdomain: tenantData.subdomain,
          name: tenantData.name,
          contactEmail: tenantData.contact_email,
          contactPhone: tenantData.contact_phone,
          streetAddress: tenantData.street_address,
          city: tenantData.city,
          stateProvince: tenantData.state_province,
          postalCode: tenantData.postal_code,
          country: tenantData.country,
          languageDefault: tenantData.language_default,
          languagesEnabled: (tenantData.languages_enabled as { [key: string]: boolean }) || { en: true, "ar-AE": false },
          rtl: tenantData.rtl,
          logoUrl: tenantData.logo_url,
          primaryColor: tenantData.primary_color,
          secondaryColor: tenantData.secondary_color,
          backgroundColor: tenantData.background_color,
          instagramUrl: tenantData.instagram_url,
          whatsappUrl: tenantData.whatsapp_url,
          googleReviewsUrl: tenantData.google_reviews_url,
          websiteUrl: tenantData.website_url,
          subscriptionPlan: tenantData.subscription_plan,
          billingStatus: tenantData.billing_status,
          monthlyQuota: tenantData.monthly_quota,
          quotaUsed: tenantData.quota_used,
          frontdeskEmail: tenantData.frontdesk_email,
          skinFlowEnabled: tenantData.skin_flow_enabled,
          hairFlowEnabled: tenantData.hair_flow_enabled,
          trustSectionEnabled: tenantData.trust_section_enabled,
        };
        
        setCurrentTenant(brand);
        setUserRole('owner'); // Grant owner role during impersonation
        setUserTenants([{ tenantId: brand.id, tenantName: brand.name, role: 'owner' }]);
        
        // Cache for the impersonation session
        localStorage.setItem(TENANT_STORAGE_KEY, brand.id);
        localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(brand));
        localStorage.setItem(USER_ROLE_CACHE_KEY, 'owner');
        
        console.log('✅ Impersonation setup complete - Tenant:', brand.name, 'Role: owner');
        console.log('🎨 Brand details loaded:', brand);
        clearTimeout(safetyTimeout);
        setIsLoading(false);
        hasLoadedOnceRef.current = true;
        loadingRef.current = false;
        return;
      }
      
      // Normal flow: Load user's tenants with optimized timeout
      const queryPromise = supabase.rpc('get_user_tenants', { 
        _user_id: userId 
      });
      
      let timeoutId: NodeJS.Timeout;
      const timeoutPromise = new Promise((_, reject) => {
        timeoutId = setTimeout(() => reject(new Error('QUERY_TIMEOUT')), 45000); // 45 seconds for tenant queries
      });
      
      let result;
      try {
        result = await Promise.race([queryPromise, timeoutPromise]);
        clearTimeout(timeoutId!);
      } catch (error: any) {
        clearTimeout(timeoutId!);
        
        if (error.message === 'QUERY_TIMEOUT') {
          console.error('⏱️ Query timeout - checking for cached data');
          
          // Try to get cached data from localStorage
          const cachedTenant = localStorage.getItem(TENANT_CACHE_KEY);
          const cachedUserTenants = localStorage.getItem(USER_TENANTS_CACHE_KEY);
          const cachedUserRole = localStorage.getItem(USER_ROLE_CACHE_KEY);
          
          // If we have EITHER existing state OR cached data, keep using it
          if ((hasLoadedOnceRef.current && currentTenant && userTenants.length > 0) || 
              (cachedTenant && cachedUserTenants)) {
            console.log('✅ Timeout but keeping existing/cached tenant data');
            
            // Restore from cache if current state is empty
            if (!currentTenant && cachedTenant) {
              setCurrentTenant(JSON.parse(cachedTenant));
              setUserTenants(JSON.parse(cachedUserTenants || '[]'));
              setUserRole(cachedUserRole || null);
            }
            
            setIsLoading(false);
            setIsInitialized(true);
            clearTimeout(safetyTimeout);
            loadingRef.current = false;
            return;
          }
          
          // Otherwise, graceful fallback: treat as no tenants found
          console.log('⏱️ Timeout with no existing data - falling back to empty state');
          result = { data: [], error: null };
        } else {
          throw error;
        }
      }
      
      const { data: tenantsData, error: tenantsError } = result as any;
      
      console.log('✅ Query completed:', { 
        dataLength: tenantsData?.length, 
        hasError: !!tenantsError,
        error: tenantsError,
        rawData: tenantsData
      });
      
      if (tenantsError) {
        console.error('❌ Query Error details:', tenantsError);
        
        // Retry once if it's a session/auth error
        if (retryCount === 0 && (
          tenantsError.message?.includes('JWT') || 
          tenantsError.message?.includes('session') ||
          tenantsError.code === 'PGRST301'
        )) {
          console.log('🔄 Retrying after error...');
          // Clear localStorage and try fresh
          localStorage.removeItem(TENANT_STORAGE_KEY);
          await new Promise(resolve => setTimeout(resolve, 1500));
          return loadTenantData(userId, tenantId, retryCount + 1);
        }
        
        // If retry failed, clear everything and let user start fresh
        console.error('❌ Failed to load tenants after retry, clearing state');
        localStorage.removeItem(TENANT_STORAGE_KEY);
        setUserTenants([]);
        setCurrentTenant(null);
        setUserRole(null);
        setIsLoading(false);
        setIsInitialized(true);
        clearTimeout(safetyTimeout);
        loadingRef.current = false;
        return;
      }

      const tenants = (tenantsData || []).map((t: any) => ({
        tenantId: t.tenant_id,
        tenantName: t.tenant_name,
        role: t.role
      }));

      console.log('📋 TenantContext: Mapped tenants:', tenants);
      setUserTenants(tenants);

      if (tenants.length === 0) {
        // User has no tenants - they need to register or be invited
        console.log('⚠️ No tenants found for user - should redirect to /admin/register');
        setCurrentTenant(null);
        setUserRole(null);
        localStorage.removeItem(TENANT_STORAGE_KEY);
        localStorage.removeItem(TENANT_CACHE_KEY);
        localStorage.removeItem(USER_TENANTS_CACHE_KEY);
        localStorage.removeItem(USER_ROLE_CACHE_KEY);
        setIsLoading(false);
        setIsInitialized(true);
        clearTimeout(safetyTimeout);
        loadingRef.current = false;
        return;
      }

      // Determine which tenant to load
      let targetTenantId = tenantId;
      if (!targetTenantId) {
        // Check localStorage first, then use first tenant
        const storedId = localStorage.getItem(TENANT_STORAGE_KEY);
        if (storedId && tenants.some(t => t.tenantId === storedId)) {
          targetTenantId = storedId;
          console.log('✅ Using stored tenant:', targetTenantId);
        } else {
          targetTenantId = tenants[0].tenantId;
          console.log('✅ Using first tenant:', targetTenantId);
        }
      }

      // Load tenant details - use maybeSingle to handle missing tenants gracefully
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', targetTenantId)
        .maybeSingle();
      
      if (tenantError) throw tenantError;

      if (!tenantData) {
        // Tenant not found - could be RLS timing issue right after login
        // Retry with longer delay if this is first attempt
        if (retryCount === 0) {
          console.log('⚠️ Tenant not found, retrying after 3s delay...');
          loadingRef.current = false; // Release lock before retry
          await new Promise(resolve => setTimeout(resolve, 3000)); // Longer delay for RLS propagation
          return loadTenantData(userId, tenantId, retryCount + 1);
        }
        
        console.log('⚠️ Tenant not found after retry:', targetTenantId);
        setCurrentTenant(null);
        setUserRole(null);
        setIsLoading(false);
        setIsInitialized(true);
        clearTimeout(safetyTimeout);
        loadingRef.current = false;
        return;
      }

      if (tenantData) {
        const brand: TenantBrand = {
          id: tenantData.id,
          subdomain: tenantData.subdomain,
          name: tenantData.name,
          contactEmail: tenantData.contact_email,
          contactPhone: tenantData.contact_phone,
          streetAddress: tenantData.street_address,
          city: tenantData.city,
          stateProvince: tenantData.state_province,
          postalCode: tenantData.postal_code,
          country: tenantData.country,
          languageDefault: tenantData.language_default,
          languagesEnabled: (tenantData.languages_enabled as { [key: string]: boolean }) || { en: true, "ar-AE": false },
          rtl: tenantData.rtl,
          logoUrl: tenantData.logo_url,
          primaryColor: tenantData.primary_color,
          secondaryColor: tenantData.secondary_color,
          backgroundColor: tenantData.background_color,
          instagramUrl: tenantData.instagram_url,
          whatsappUrl: tenantData.whatsapp_url,
          googleReviewsUrl: tenantData.google_reviews_url,
          websiteUrl: tenantData.website_url,
          subscriptionPlan: tenantData.subscription_plan,
          billingStatus: tenantData.billing_status,
          monthlyQuota: tenantData.monthly_quota,
          quotaUsed: tenantData.quota_used,
          frontdeskEmail: tenantData.frontdesk_email,
          skinFlowEnabled: tenantData.skin_flow_enabled,
          hairFlowEnabled: tenantData.hair_flow_enabled,
          trustSectionEnabled: tenantData.trust_section_enabled,
        };

        setCurrentTenant(brand);

        // Find user's role in this tenant
        const tenantRole = tenants.find(t => t.tenantId === targetTenantId);
        
        console.log('👤 TenantContext: Setting role:', tenantRole?.role, 
          'for tenant:', targetTenantId);
        setUserRole(tenantRole?.role || null);

        // Cache everything in localStorage for fast recovery on refresh
        localStorage.setItem(TENANT_STORAGE_KEY, targetTenantId);
        localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(brand));
        localStorage.setItem(USER_TENANTS_CACHE_KEY, JSON.stringify(tenants));
        localStorage.setItem(USER_ROLE_CACHE_KEY, tenantRole?.role || '');
        hasLoadedOnceRef.current = true; // Mark as successfully loaded
        console.log('✅ TenantContext: Complete setup - Tenant:', brand.name, 'Role:', tenantRole?.role);
      }
      
      // Set loading to false after successful load
      clearTimeout(safetyTimeout);
      setIsLoading(false);
      setIsInitialized(true);
      loadingRef.current = false;
    } catch (error: any) {
      console.error('❌ TenantContext error:', error);
      clearTimeout(safetyTimeout);
      
      // Check if it's a timeout error
      if (error.message?.includes('QUERY_TIMEOUT')) {
        console.error('⏱️ Query timeout detected');
        
        // Try to restore from localStorage if available
        const cachedTenant = localStorage.getItem(TENANT_CACHE_KEY);
        const cachedUserTenants = localStorage.getItem(USER_TENANTS_CACHE_KEY);
        const cachedUserRole = localStorage.getItem(USER_ROLE_CACHE_KEY);
        
        if (cachedTenant && cachedUserTenants && cachedUserRole) {
          console.log('⚠️ Timeout - restoring from localStorage cache');
          setCurrentTenant(JSON.parse(cachedTenant));
          setUserTenants(JSON.parse(cachedUserTenants));
          setUserRole(cachedUserRole);
          setIsLoading(false);
          setIsInitialized(true);
          loadingRef.current = false;
          return;
        }
        
        // If we have in-memory data, keep using it
        if (currentTenant && userRole) {
          console.log('⚠️ Timeout but keeping in-memory cached data:', currentTenant.name);
          setIsLoading(false);
          setIsInitialized(true);
          loadingRef.current = false;
          return;
        }
      }
      
      // Check if it's an auth error and we can retry
      if (retryCount === 0 && (
        error.message?.includes('JWT') || 
        error.message?.includes('session')
      )) {
        console.log('🔄 Auth error, retrying with fresh state...');
        localStorage.removeItem(TENANT_STORAGE_KEY);
        loadingRef.current = false;
        await new Promise(resolve => setTimeout(resolve, 2000));
        return loadTenantData(userId, tenantId, retryCount + 1);
      }
      
      // On timeout or persistent error, keep existing tenant data if available
      if (currentTenant && userRole) {
        console.log('⚠️ Error loading tenant but keeping existing data:', currentTenant.name);
        setIsLoading(false);
        setIsInitialized(true);
        loadingRef.current = false;
        // Don't show error toast if we have working data
        return; // Keep existing state
      }
      
      // Only clear everything if we have no tenant data at all
      console.log('❌ Error loading tenant and no existing data, clearing state');
      localStorage.removeItem(TENANT_STORAGE_KEY);
      localStorage.removeItem(TENANT_CACHE_KEY);
      localStorage.removeItem(USER_TENANTS_CACHE_KEY);
      localStorage.removeItem(USER_ROLE_CACHE_KEY);
      setCurrentTenant(null);
      setUserTenants([]);
      setUserRole(null);
      setIsLoading(false);
      setIsInitialized(true);
      loadingRef.current = false;
      
      // Only show toast for non-auth, non-timeout errors
      if (!error.message?.includes('JWT') && 
          !error.message?.includes('session') &&
          !error.message?.includes('QUERY_TIMEOUT')) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: error.message || 'Failed to load tenant information',
        });
      }
    } finally {
      clearTimeout(safetyTimeout);
      loadingRef.current = false;
    }
  };

  useEffect(() => {
    let mounted = true;
    let isInitializing = false;
    
    const initializeTenant = async () => {
      if (isInitializing) {
        console.log('⏸️ Initialization already in progress');
        return;
      }
      
      isInitializing = true;
      
      try {
        console.log('🚀 TenantContext: Initializing...');
        
        // Try to restore from cache immediately for faster UX
        const cachedTenant = localStorage.getItem(TENANT_CACHE_KEY);
        const cachedUserTenants = localStorage.getItem(USER_TENANTS_CACHE_KEY);
        const cachedUserRole = localStorage.getItem(USER_ROLE_CACHE_KEY);
        
        if (cachedTenant && cachedUserTenants && cachedUserRole) {
          console.log('📦 TenantContext: Restoring from cache...');
          setCurrentTenant(JSON.parse(cachedTenant));
          setUserTenants(JSON.parse(cachedUserTenants));
          setUserRole(cachedUserRole);
          setIsLoading(false);
          setIsInitialized(true);
          hasLoadedOnceRef.current = true; // Mark as loaded to prevent clearing on timeout
          console.log('✅ TenantContext: Cache restored, loading fresh data in background...');
        }
        
        const { data: { session } } = await supabase.auth.getSession();
        console.log('👤 TenantContext: Session user ID:', session?.user?.id);
        
        if (!mounted) {
          console.log('⚠️ TenantContext: Component unmounted during init');
          isInitializing = false;
          return;
        }
        
        if (session?.user) {
          console.log('🔄 TenantContext: Loading fresh tenant data...');
          await loadTenantData(session.user.id);
          setIsInitialized(true);
          console.log('✅ TenantContext: Fresh tenant data loaded');
        } else {
          // Check for impersonation mode first
          const impersonationMode = sessionStorage.getItem('impersonation_mode');
          const impersonationTenant = sessionStorage.getItem('impersonation_tenant');
          
          if (impersonationMode && impersonationTenant) {
            console.log('🎭 TenantContext: Impersonation mode detected, loading tenant directly');
            // Load the impersonated tenant without requiring authentication
            await loadTenantData('', impersonationTenant);
            setIsInitialized(true);
            console.log('✅ TenantContext: Impersonated tenant loaded');
          } else {
            console.log('🌐 TenantContext: No session, loading public tenant data...');
            // Load public tenant data for unauthenticated users
            try {
              // Check for subdomain from path-based routing (sessionStorage)
              const clinicSubdomain = sessionStorage.getItem('clinic_subdomain');
              
              // Check if there's a tenant ID in the URL query params
              const urlParams = new URLSearchParams(window.location.search);
              const tenantIdFromUrl = urlParams.get('tenant');
              
              // Query for the specific tenant or the first available tenant using the public branding view
              const query = supabase
                .from('tenant_public_branding')
                .select('*');
              
              if (clinicSubdomain) {
                // Load by subdomain from path-based routing
                query.eq('subdomain', clinicSubdomain);
              } else if (tenantIdFromUrl) {
                // Load by tenant ID from query param
                query.eq('id', tenantIdFromUrl);
              } else {
                // Load first available tenant
                query.order('id', { ascending: true }).limit(1);
              }
              
              const { data: publicTenant, error: publicTenantError } = await query.single();

              if (publicTenantError) {
                throw publicTenantError;
              }

              if (publicTenant) {
                const brand: TenantBrand = {
                  id: publicTenant.id,
                  subdomain: publicTenant.subdomain || '',
                  name: publicTenant.name,
                  contactEmail: '', // Not available in public view
                  contactPhone: '', // Not available in public view
                  streetAddress: '', // Not available in public view
                  city: '', // Not available in public view
                  stateProvince: '', // Not available in public view
                  postalCode: '', // Not available in public view
                  country: '', // Not available in public view
                  languageDefault: publicTenant.language_default,
                  languagesEnabled: (publicTenant.languages_enabled as { [key: string]: boolean }) || {},
                  rtl: publicTenant.rtl,
                  logoUrl: publicTenant.logo_url || '',
                  primaryColor: publicTenant.primary_color,
                  secondaryColor: publicTenant.secondary_color,
                  backgroundColor: publicTenant.background_color,
                  instagramUrl: '', // Not available in public view
                  whatsappUrl: '', // Not available in public view
                  googleReviewsUrl: '', // Not available in public view
                  websiteUrl: '', // Not available in public view
                  subscriptionPlan: 'starter', // Default for public view
                  billingStatus: 'active', // Default for public view
                  monthlyQuota: 0, // Not available in public view
                  quotaUsed: 0, // Not available in public view
                  frontdeskEmail: '', // Not available in public view
                  skinFlowEnabled: publicTenant.skin_flow_enabled,
                  hairFlowEnabled: publicTenant.hair_flow_enabled,
                  trustSectionEnabled: publicTenant.trust_section_enabled,
                };

                setCurrentTenant(brand);
                localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(brand));
                console.log('✅ TenantContext: Public tenant loaded:', brand.name);
              }
            } catch (error: any) {
              console.error('❌ TenantContext: Error loading public tenant:', error);
            }
            
            setIsLoading(false);
            setIsInitialized(true);
            loadingRef.current = false;
          }
        }
      } catch (error) {
        console.error('❌ TenantContext: Initialization error:', error);
        if (mounted) {
          // Keep cached data even if refresh fails
          if (!currentTenant) {
            setCurrentTenant(null);
            setUserTenants([]);
            setUserRole(null);
          }
          setIsLoading(false);
          setIsInitialized(true);
          loadingRef.current = false;
        }
      } finally {
        isInitializing = false;
      }
    };

    // Set up auth listener FIRST before initializing
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔐 TenantContext: Auth state changed:', event, 'hasLoadedOnce:', hasLoadedOnceRef.current);
      if (!mounted) return;
      
      // Handle INITIAL_SESSION separately - don't reload if we already have data
      if (event === 'INITIAL_SESSION') {
        console.log('📊 Initial session detected - deferring to initializeTenant');
        return; // Let initializeTenant handle the initial load
      }
      
      if (event === 'SIGNED_IN' && session?.user) {
        // Only reload if we haven't loaded successfully before
        // Once we've loaded successfully, keep using that data to prevent refresh loops
        if (!hasLoadedOnceRef.current) {
          console.log('✅ User signed in, loading fresh tenant data');
          sessionStorage.removeItem('impersonation_mode');
          sessionStorage.removeItem('impersonation_tenant');
          sessionStorage.removeItem('impersonation_token');
          setIsLoading(true);
          await loadTenantData(session.user.id);
        } else {
          console.log('✅ User already authenticated with tenant data, skipping reload');
        }
      } else if (event === 'TOKEN_REFRESHED' && session?.user) {
        // Never reload on token refresh - keep using cached data
        console.log('🔄 Token refreshed - keeping existing tenant data');
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 User signed out, clearing all state');
        localStorage.removeItem(TENANT_STORAGE_KEY);
        localStorage.removeItem(TENANT_CACHE_KEY);
        localStorage.removeItem(USER_TENANTS_CACHE_KEY);
        localStorage.removeItem(USER_ROLE_CACHE_KEY);
        setCurrentTenant(null);
        setUserTenants([]);
        setUserRole(null);
        setIsLoading(false);
        setIsInitialized(false);
        loadingRef.current = false;
        hasLoadedOnceRef.current = false; // Reset on sign out
      }
    });

    // Initialize after auth listener is set up
    initializeTenant();

    return () => {
      console.log('🧹 TenantContext: Cleaning up...');
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const switchTenant = async (tenantId: string) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      setIsLoading(true);
      await loadTenantData(session.user.id, tenantId);
    }
  };

  const refreshTenant = async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user && currentTenant) {
      await loadTenantData(session.user.id, currentTenant.id);
    }
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        userTenants,
        userRole,
        isLoading,
        isInitialized,
        switchTenant,
        refreshTenant,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};
